import {
  companyOptions,
  getImageOptions,
  getMollieAuthUrl,
  getUserByEmail,
  handleMollieOAuthCallback,
  handleUploadImage,
  snackSupplierOptions,
  updateFoodSupplierSettings,
  updateSnackSupplier,
  useBuilding,
  useUserByEmailOptions,
} from "@/api";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import {
  Button,
  Checkbox,
  Chip,
  Dropdown,
  Icon,
  ImageInput,
  InputField,
  LoadingSpinner,
  PhoneInput,
  PopupCard,
  SelectRow,
  StrictNumberInput,
  TimeInput,
  Toggle,
} from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "@/styles/Snack.module.scss";
import companySettingsStyles from "@/styles/CompanySettings.module.scss";
import { ReactFormExtendedApi, useForm, useStore } from "@tanstack/react-form";
import {
  CutoffSettings,
  ExpandedCutoffTimeString,
  NotificationType,
  SnackDeliveryMethod,
  SnackSupplier,
  SnackSupplierFormType,
  TimeString,
} from "@/types";
import { usePrompt } from "@/hooks/usePrompt";
import SaveResetButtons from "@/components/SaveResetButtons";
import { PersonSearch } from "@/components/PersonSearch";
import SupplierAdminsSection from "@/components/food_admin/SupplierAdminsSection";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import {
  SUPPORTED_PHONE_COUNTRY_CODES,
  getBuildingId,
  isSnackOrderDeliveryMethod,
  isSnackOrderPickupMethod,
  notifyApiError,
  isSnackOrderWithServiceType,
  notifyImageUploadError,
  isTimeString,
  timeInputStringToDate,
  EMAIL_REGEX,
} from "@/lib";
import { isBefore } from "date-fns";
import { motion } from "framer-motion";
import { useBuildingQuery } from "@/components/RouterSetup";
import OrderCutoffSettings from "@/components/snack/OrderCutoffSettings";
import {
  DEFAULT_CUTOFF_SETTINGS,
  isSameCutoffSettings,
} from "@/lib/snackCutoff";
import { getBuildingIdFromMollieState } from "@/lib/food";
import { useEditBaseline } from "@/hooks/useEditBaseline";
import { GuestLunchQr } from "@/components/food_admin/GuestLunchQr";

// Emails are stored in the existing `email` field as a comma-joined string so the
// backend contract stays the same. Parse/join at the UI edge.
const MAX_ADMIN_EMAILS = 5;
const MAX_EMAIL_LENGTH = 60;
const parseEmails = (value?: string | null): string[] =>
  (value ?? "")
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);
const joinEmails = (list: string[]): string => list.join(",");

export const Route = createFileRoute("/_portal/food-admin/settings")({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    status?: string;
    message?: string;
    code?: string;
    state?: string;
  } => ({
    status: typeof search.status === "string" ? search.status : undefined,
    message: typeof search.message === "string" ? search.message : undefined,
    code: typeof search.code === "string" ? search.code : undefined,
    state: typeof search.state === "string" ? search.state : undefined,
  }),
  loader: () => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(companyOptions(supplierCompanyId));
    queryClient.ensureQueryData(snackSupplierOptions(supplierCompanyId));
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: building } = useBuilding();
  // This shared page accepts either role, even when both products are enabled.
  const { allowSnackAdminAccess } = useWelcomeSnackAccess(true);
  const { allowFoodAdminAccess } = useWelcomeFoodAccess(true);
  const navigate = useNavigate();
  useEffect(() => {
    if (!allowSnackAdminAccess && !allowFoodAdminAccess) {
      navigate({ to: "/401", replace: true });
    }
  }, [allowSnackAdminAccess, allowFoodAdminAccess, navigate]);
  const { addNotification } = useSnackbarStore();
  const { data: supplier, isLoading } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const { data: supplierCompany, isLoading: isLoadingCompany } =
    useBuildingQuery(companyOptions(building.supplierCompanyId));
  const { data: supplierLogo } = useBuildingQuery(
    getImageOptions(supplier?.logoUrl),
  );

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  usePrompt({
    isOpen: hasUnsavedChanges,
  });

  const [tab, setTab] = useState<"snack" | "food">("snack");
  const cutoffEdit = useEditBaseline<CutoffSettings>(isSameCutoffSettings);
  const closedOnWeekendsEdit = useEditBaseline<boolean>();
  const clearEdits = () => {
    cutoffEdit.clear();
    closedOnWeekendsEdit.clear();
  };

  const form = useForm<SnackSupplierFormType>({
    defaultValues: {
      logo: { previewUrl: supplierLogo ?? supplier?.logoUrl ?? "" },
      name: supplier?.name || "",
      address:
        supplier?.address ||
        (building.address
          ? `${building.address.street}, ${building.address.postalCode} ${building.address.city}, ${building.address.country}`
          : ""),
      email: supplier?.email || "",
      phone: supplier?.phone || "",
      orderCutoffTime: supplier?.orderCutoffTime || null,
      cutoffSettings: supplier?.cutoffSettings ?? DEFAULT_CUTOFF_SETTINGS,
      closedOnWeekends: supplier?.closedOnWeekends ?? false,
      deliveryTypes: supplier?.deliveryTypes || [],
      pickupSettings: supplier?.pickupSettings || {
        pickupFee: 0,
        pickupOpenAt: "07:00" as TimeString,
        pickupClosedAt: "17:00" as TimeString,
        pickupLocation: "",
      },
      deliverySettings: supplier?.deliverySettings || {
        deliveryFee: 0,
        deliveryOpenAt: "08:00" as TimeString,
        deliveryClosedAt: "16:00" as TimeString,
      },
      autoAcceptOrders: supplier?.autoAcceptOrders || false,
      autoPrepareOrders: supplier?.autoPrepareOrders || false,
      autoCompleteOrders: supplier?.autoCompleteOrders || false,
      externalLocationId: supplier?.externalLocationId || "",
      wlcmFoodSettings: {
        openingLunchTime: (supplier?.wlcmFoodSettings?.openingLunchTime ||
          "11:00") as TimeString,
        closingLunchTime: (supplier?.wlcmFoodSettings?.closingLunchTime ||
          "13:00") as TimeString,
        guestLunchGrossPrice:
          supplier?.wlcmFoodSettings?.guestLunchGrossPrice || null,
      },
    },
    onSubmit: async ({ value }) => {
      try {
        const { logo, cutoffSettings, closedOnWeekends, ...rest } = value;
        const updateData = rest as Partial<SnackSupplier>;

        // cutoff-v1 rejects the whole save if orderCutoffTime changes, and
        // treats a sent cutoffSettings or closedOnWeekends as an explicit
        // change. Legacy mode rejects both.
        if (supplier?.orderingMode === "cutoff-v1") {
          delete updateData.orderCutoffTime;
          if (cutoffEdit.isEdited(cutoffSettings)) {
            updateData.cutoffSettings = cutoffSettings;
          }
          if (closedOnWeekendsEdit.isEdited(closedOnWeekends)) {
            updateData.closedOnWeekends = closedOnWeekends;
          }
        }

        if (logo?.encoded) {
          const url = await handleUploadImage(logo.encoded, (error) =>
            notifyImageUploadError(error, addNotification, t),
          );
          updateData.logoUrl = url || supplier?.logoUrl || "";
        }

        if (allowSnackAdminAccess) {
          if (!allowFoodAdminAccess) {
            delete updateData.wlcmFoodSettings;
          }
          updateData.deliveryTypes = updateData.deliveryTypes?.filter(
            isSnackOrderWithServiceType,
          );
          await updateSnackSupplier(updateData);
        } else {
          // The shared form also has hidden Snack defaults. Food-only saves
          // project just the fields authorized by the dedicated Food route.
          await updateFoodSupplierSettings({
            name: updateData.name,
            address: updateData.address,
            email: updateData.email,
            phone: updateData.phone,
            logoUrl: updateData.logoUrl,
            externalLocationId: updateData.externalLocationId,
            wlcmFoodSettings: {
              openingLunchTime: updateData.wlcmFoodSettings?.openingLunchTime,
              closingLunchTime: updateData.wlcmFoodSettings?.closingLunchTime,
              guestLunchGrossPrice:
                updateData.wlcmFoodSettings?.guestLunchGrossPrice,
            },
          });
        }

        clearEdits();
        setHasUnsavedChanges(false);
        addNotification(
          t("NOTIFICATIONS.DOCUMENT_UPDATED", {
            entity: t("SNACK_ADMIN.SETTINGS.HEADER").toLowerCase(),
          }),
          NotificationType.SUCCESS,
        );
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "supplier", building.supplierCompanyId],
        });
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "menu-items", building.supplierCompanyId],
        });
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error(error);
        notifyApiError(error, addNotification, t, {
          properties: {
            entity: t("SNACK_ADMIN.SETTINGS.HEADER").toLowerCase(),
          },
          fallbackKey: "UPDATE_FAILED",
        });
      }
    },
  });

  // Track the current form value (not just the initial supplier one) so the
  // "Chosen contact person" indicator refetches when the admin edits the list.
  const currentEmailValue = useStore(form.store, (state) => state.values.email);
  const currentEmails = parseEmails(currentEmailValue);
  const { data: contactPerson } = useBuildingQuery(
    useUserByEmailOptions(
      currentEmails.length === 1 ? currentEmails[0] : undefined,
    ),
  );
  const [showPersonSelector, setShowPersonSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  // Bumping this key remounts PersonSearch to clear its internal selection for the next pick
  const [personSearchKey, setPersonSearchKey] = useState(0);
  const searchForUser = async (text: string) => {
    if (!text) {
      return [];
    }
    setIsSearching(true);
    try {
      return await getUserByEmail(text, building.supplierCompanyId, true);
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsSearching(false);
    }
  };
  const addEmailToList = (email: string) => {
    const list = parseEmails(form.getFieldValue("email"));
    const normalized = email.toLowerCase();
    if (
      !normalized ||
      list.some((current) => current.toLowerCase() === normalized) ||
      list.length >= MAX_ADMIN_EMAILS ||
      normalized.length > MAX_EMAIL_LENGTH ||
      !EMAIL_REGEX.test(normalized)
    )
      return;
    form.setFieldValue("email", joinEmails([...list, normalized]));
    // setFieldValue does not fire <form> onChange, so mark dirty ourselves
    setTimeout(() => setHasUnsavedChanges(true), 30);
  };
  const removeEmailFromList = (email: string) => {
    const list = parseEmails(form.getFieldValue("email"));
    const target = email.toLowerCase();
    if (!list.some((current) => current.toLowerCase() === target)) return;
    form.setFieldValue(
      "email",
      joinEmails(list.filter((current) => current.toLowerCase() !== target)),
    );
    setTimeout(() => setHasUnsavedChanges(true), 30);
  };
  const promoteEmailToFront = (email: string) => {
    const list = parseEmails(form.getFieldValue("email"));
    const target = email.toLowerCase();
    if (
      list[0]?.toLowerCase() === target ||
      !list.some((current) => current.toLowerCase() === target)
    )
      return;
    const match = list.find((current) => current.toLowerCase() === target);
    if (!match) return;
    form.setFieldValue(
      "email",
      joinEmails([
        match,
        ...list.filter((current) => current.toLowerCase() !== target),
      ]),
    );
    setTimeout(() => setHasUnsavedChanges(true), 30);
  };

  useEffect(() => {
    if (!isLoading && !supplier) {
      addNotification("SUPPLIER NOT FOUND");
      navigate({ to: "/404" });
    }
  }, [supplier, isLoading]);

  const {
    status: mollieStatus,
    code: mollieCode,
    state: mollieState,
  } = Route.useSearch();
  const mollieExchangeStarted = useRef(false);
  const [isExchangingMollie, setIsExchangingMollie] = useState(false);

  useEffect(() => {
    if (!mollieCode || !mollieState || mollieExchangeStarted.current) return;

    const buildingIdFromState = getBuildingIdFromMollieState(mollieState);
    if (!buildingIdFromState) {
      addNotification(
        t("NOTIFICATIONS.SERVER_ERROR"),
        NotificationType.WARNING,
      );
      navigate({
        to: "/food-admin/settings",
        search: () => ({
          status: "error",
          message: undefined,
          code: undefined,
          state: undefined,
          building: undefined,
        }),
        replace: true,
      });
      return;
    }

    if (getBuildingId() !== buildingIdFromState) {
      navigate({
        to: "/food-admin/settings",
        search: () => ({
          status: undefined,
          message: undefined,
          building: buildingIdFromState,
          code: mollieCode,
          state: mollieState,
        }),
        replace: true,
      });
      return;
    }

    mollieExchangeStarted.current = true;
    setIsExchangingMollie(true);

    handleMollieOAuthCallback(mollieCode, mollieState)
      .then(({ redirectUrl }) => {
        window.location.href = redirectUrl;
      })
      .catch(() => {
        mollieExchangeStarted.current = false;
        setIsExchangingMollie(false);
        addNotification(
          t("NOTIFICATIONS.SERVER_ERROR"),
          NotificationType.WARNING,
        );
        navigate({
          to: "/food-admin/settings",
          search: () => ({
            status: "error",
            message: undefined,
            code: undefined,
            state: undefined,
            building: undefined,
          }),
          replace: true,
        });
      });
  }, [mollieCode, mollieState, addNotification, navigate, t]);

  useEffect(() => {
    if (mollieStatus === "connected") {
      addNotification(
        t("FOOD_ADMIN.SETTINGS.MOLLIE_CONNECT_SUCCESS"),
        NotificationType.SUCCESS,
      );
      navigate({
        to: "/food-admin/settings",
        search: () => ({
          status: undefined,
          message: undefined,
          code: undefined,
          state: undefined,
          building: undefined,
        }),
        replace: true,
      });
    } else if (mollieStatus === "error") {
      addNotification(t("FOOD_ADMIN.SETTINGS.MOLLIE_CONNECTION_FAILED"));
      navigate({
        to: "/food-admin/settings",
        search: () => ({
          status: undefined,
          message: undefined,
          code: undefined,
          state: undefined,
          building: undefined,
        }),
        replace: true,
      });
    }
  }, [mollieStatus, addNotification, navigate, t]);

  if (!allowSnackAdminAccess && !allowFoodAdminAccess) return null;

  if (isExchangingMollie) {
    return (
      <div className="center">
        <LoadingSpinner />
        <p style={{ marginTop: "var(--wlcm-spacing-md)" }}>
          {t("FOOD_ADMIN.SETTINGS.CONNECTING_MOLLIE")}
        </p>
      </div>
    );
  }

  return isLoading ? (
    <div className="center">
      <LoadingSpinner />
    </div>
  ) : (
    <>
      <div className="pageHeader">
        <h1>{t("SNACK_ADMIN.SETTINGS.HEADER")}</h1>
        <p>
          {t("SNACK_ADMIN.SETTINGS.SUBHEADER", {
            companyName: isLoadingCompany
              ? "..."
              : supplierCompany
                ? supplierCompany.buildingTenantName
                : building.displayName,
          })}
        </p>
      </div>
      <form
        onChange={() => setHasUnsavedChanges(true)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className={styles.supplierSettingsContainer}>
          <form.Field name="logo">
            {(field) => (
              <ImageInput
                key={field.state.value?.previewUrl}
                id="supplier-logo"
                strategy="cover"
                placeholder={
                  <div className={styles.logoPlaceholder}>
                    <Icon name="add_photo" />
                    <p>{t("IMAGE.PLACEHOLDER.LOGO")}</p>
                  </div>
                }
                translations={{
                  uploadImageOverlayText: t("IMAGE.UPLOAD"),
                  replaceImageAriaLabel: t("IMAGE.REPLACE.ARIA_LABEL"),
                  replaceImageOverlayText: t("IMAGE.REPLACE.TEXT"),
                  undoChangeButtonText: t("IMAGE.UNDO"),
                  editDoneText: t("IMAGE.DONE"),
                  editCancelText: t("IMAGE.CANCEL"),
                  fileTooLargeText: t("IMAGE.FILE_TOO_LARGE"),
                  dropZoneText: t("IMAGE.DROP_ZONE_TEXT"),
                }}
                onChange={(image) => {
                  field.handleChange(image);
                  setHasUnsavedChanges(true);
                }}
                onError={(error, type) =>
                  addNotification(
                    t(`NOTIFICATIONS.${error}`),
                    type as NotificationType,
                  )
                }
                onUndo={(previewUrl) => {
                  field.handleChange({ previewUrl, encoded: "" });
                }}
                originalImageUrl={supplierLogo ?? supplier?.logoUrl}
                currImageUrl={field.state.value?.previewUrl}
                style={{
                  previewStyle: {
                    image: {
                      borderRadius: "var(--wlcm-border-radius-sm)",
                      overflow: "hidden",
                    },
                  },
                  overlayStyle: {
                    borderRadius: "var(--wlcm-border-radius-sm)",
                  },
                  containerStyle: {
                    height: 200,
                    width: 200,
                    margin: "0 auto",
                  },
                }}
              />
            )}
          </form.Field>
          <div className={styles.supplierSettingsFields}>
            <div>
              <form.Field name="name">
                {(field) => (
                  <InputField
                    large
                    label={t("USERS.TABLE_HEADERS.NAME")}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    dangerText={field.state.meta.errors.toString()}
                  />
                )}
              </form.Field>
              <div className={styles.spacerDiv}>{/* Empty for spacing */}</div>
            </div>
            <form.Field name="address">
              {(field) => (
                <InputField
                  label={t("BUILDING_SETTINGS.ADDRESS")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  dangerText={field.state.meta.errors.toString()}
                />
              )}
            </form.Field>
          </div>
        </div>
        <fieldset className={styles.contactDetailsFieldset}>
          <div className={styles.fieldsetLegend}>
            <legend>{t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.LABEL")}</legend>
            <p>{t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.DESCRIPTION")}</p>
            <form.Subscribe
              selector={({ values }) => [values.phone, values.email]}
              children={([phoneFormValue, emailFormValue]) => {
                const emails = parseEmails(emailFormValue);
                if (
                  emails.length !== 1 ||
                  !contactPerson ||
                  contactPerson.email?.toLowerCase() !==
                    emails[0].toLowerCase() ||
                  contactPerson.phone !== phoneFormValue
                )
                  return null;
                return (
                  <p>
                    {t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.CHOSEN_PERSON")}:{" "}
                    <strong>
                      {contactPerson.firstName} {contactPerson.lastName}
                    </strong>
                  </p>
                );
              }}
            />
          </div>
          {/* Subscribe (not Field) — Field's render prop doesn't re-fire when the
              popup / X button mutates the value via form.setFieldValue */}
          <form.Subscribe
            selector={({ values }) => values.email}
            children={(emailValue) => (
              <EmailChipsField
                emails={parseEmails(emailValue)}
                onAdd={addEmailToList}
                onRemove={removeEmailFromList}
                onPromote={promoteEmailToFront}
                leadingNode={
                  <Button
                    icon="search"
                    onClick={() => {
                      setShowPersonSelector(true);
                      setPersonSearchKey((k) => k + 1);
                    }}
                    style={{ height: 36 }}
                  >
                    {t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.FIND_USER")}
                  </Button>
                }
              />
            )}
          />
          <form.Field name="phone">
            {(field) => (
              <PhoneInput
                icon="phone_android"
                countries={SUPPORTED_PHONE_COUNTRY_CODES}
                label={t("ADD_USER.PHONE")}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                dangerText={field.state.meta.errors.toString()}
              />
            )}
          </form.Field>
        </fieldset>
        <div style={{ marginTop: "var(--wlcm-spacing-md)" }}>
          <h3>{t("FOOD_ADMIN.SETTINGS.EXTERNAL_LOCATION_ID")}</h3>
          <p style={{ opacity: 0.5, marginBottom: "var(--wlcm-spacing-xs)" }}>
            {t("FOOD_ADMIN.SETTINGS.EXTERNAL_LOCATION_ID_DESCRIPTION")}
          </p>
          <form.Field name="externalLocationId">
            {(field) => (
              <InputField
                compact
                label={t("FOOD_ADMIN.SETTINGS.EXTERNAL_LOCATION_ID")}
                hideLabel
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                maxLength={100}
                style={{ maxWidth: 400 }}
              />
            )}
          </form.Field>
        </div>

        {allowSnackAdminAccess && !allowFoodAdminAccess ? (
          <SnackSettingsTab
            form={form}
            setHasUnsavedChanges={setHasUnsavedChanges}
            onCutoffEdit={cutoffEdit.track}
            onClosedOnWeekendsEdit={closedOnWeekendsEdit.track}
          />
        ) : allowFoodAdminAccess && !allowSnackAdminAccess ? (
          <FoodSettingsTab form={form} />
        ) : allowSnackAdminAccess && allowFoodAdminAccess ? (
          <div className={styles.tabsContainer}>
            <SelectRow
              tabs
              label="Tabs"
              hideLabel
              name="food-settings-tab"
              options={[
                {
                  value: "snack",
                  label: t(
                    "BUILDING_SETTINGS.BOOKING.EDIT_ROOM.MEETING_FOOD.HEADER",
                  ),
                  icon: "burger",
                },
                {
                  value: "food",
                  label: t("FOOD_ADMIN.RESTAURANT"),
                  icon: "restaurant",
                },
              ]}
              value={tab}
              onChange={(newTab) => setTab(newTab as "snack" | "food")}
              style={{
                width: "max-content",
                margin: "0 auto",
              }}
            />
            {tab === "snack" ? (
              <SnackSettingsTab
                form={form}
                setHasUnsavedChanges={setHasUnsavedChanges}
                onCutoffEdit={cutoffEdit.track}
                onClosedOnWeekendsEdit={closedOnWeekendsEdit.track}
              />
            ) : (
              <FoodSettingsTab form={form} />
            )}
          </div>
        ) : null}

        <SaveResetButtons
          form={form}
          showButtons={hasUnsavedChanges}
          onHideButtons={() => setHasUnsavedChanges(false)}
          onReset={clearEdits}
          disabledSubmit={!hasUnsavedChanges}
        />
      </form>
      <PopupCard
        open={showPersonSelector}
        onClose={() => {
          setShowPersonSelector(false);
          setSearchQuery("");
        }}
      >
        <form.Subscribe
          selector={({ values }) => values.email}
          children={(emailValue) => {
            const emails = parseEmails(emailValue);
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--wlcm-spacing-sm)",
                }}
              >
                <div>
                  <h3 style={{ marginBottom: "var(--wlcm-spacing-xs)" }}>
                    {t(
                      "SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.MODAL.EMAILS_HEADER",
                    )}{" "}
                    ({emails.length}/{MAX_ADMIN_EMAILS})
                  </h3>
                  {emails.length === 0 ? (
                    <p style={{ opacity: 0.7 }}>
                      {t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.NO_EMAILS")}
                    </p>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "var(--wlcm-spacing-xs)",
                      }}
                    >
                      {emails.map((email, index) => (
                        <EmailChip
                          key={`${email}-${index}`}
                          email={email}
                          index={index}
                          dark
                          onRemove={removeEmailFromList}
                          onPromote={promoteEmailToFront}
                        />
                      ))}
                    </div>
                  )}
                  {emails.length >= MAX_ADMIN_EMAILS && (
                    <p
                      style={{
                        opacity: 0.7,
                        marginTop: 4,
                        fontSize: "var(--wlcm-text-sm)",
                      }}
                    >
                      {t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.MAX_REACHED")}
                    </p>
                  )}
                  {emails.length > 1 && (
                    <p
                      style={{
                        opacity: 0.7,
                        marginTop: 4,
                        fontSize: "var(--wlcm-text-sm)",
                      }}
                    >
                      {t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.PROMOTE_HINT")}
                    </p>
                  )}
                </div>
                <PersonSearch
                  // key bump resets the dropdown after each pick so admin can keep adding
                  key={personSearchKey}
                  dark
                  header={t(
                    "SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.MODAL.HEADER",
                  )}
                  body={t(
                    "SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.MODAL.SUBHEADER",
                  )}
                  noResultsText={t(
                    "SNACK_ADMIN.NEW_ORDER.ON_BEHALF_OF.NO_RESULTS",
                  )}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  isSearching={isSearching}
                  searchForUser={searchForUser}
                  addUser={(user) => {
                    if (user.email) addEmailToList(user.email);
                    setSearchQuery("");
                    setPersonSearchKey((k) => k + 1);
                  }}
                  extraPaddingBottom
                />
              </div>
            );
          }}
        />
      </PopupCard>
    </>
  );
}

const SnackSettingsTab = ({
  form,
  setHasUnsavedChanges,
  onCutoffEdit,
  onClosedOnWeekendsEdit,
}: {
  form: ReactFormExtendedApi<SnackSupplierFormType, undefined>;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  /** Receives the cut-off value from before the edit */
  onCutoffEdit: (current: CutoffSettings) => void;
  /** Receives the checkbox value from before the edit */
  onClosedOnWeekendsEdit: (current: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { data: building } = useBuilding();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );

  const requireTime =
    (labelKey: string) =>
    ({ value }: { value?: string }) =>
      isTimeString(value)
        ? undefined
        : t("VALIDATION.REQUIRED", { field: t(labelKey) });
  const requireOpensAt = requireTime(
    "BUILDING_SETTINGS.BOOKING.EDIT_ROOM.OPENING_HOURS.OPENS_AT",
  );
  const requireClosesAt = requireTime(
    "BUILDING_SETTINGS.BOOKING.EDIT_ROOM.OPENING_HOURS.CLOSES_AT",
  );

  // Hidden fields keep their errors, so a time cleared before turning the
  // method off would block saving with nothing on screen to fix
  const restoreClearedTimes = (method: "pickup" | "delivery") => {
    const saved = form.options.defaultValues;
    const fields =
      method === "pickup"
        ? ([
            ["pickupSettings.pickupOpenAt", saved?.pickupSettings.pickupOpenAt],
            [
              "pickupSettings.pickupClosedAt",
              saved?.pickupSettings.pickupClosedAt,
            ],
          ] as const)
        : ([
            [
              "deliverySettings.deliveryOpenAt",
              saved?.deliverySettings.deliveryOpenAt,
            ],
            [
              "deliverySettings.deliveryClosedAt",
              saved?.deliverySettings.deliveryClosedAt,
            ],
          ] as const);
    fields.forEach(([name, savedTime]) => {
      if (isTimeString(form.getFieldValue(name))) return;
      form.setFieldValue(name, savedTime);
      form.setFieldMeta(name, (prev) => ({ ...prev, errorMap: {} }));
    });
  };

  const automationToggles: {
    label: string;
    field: keyof SnackSupplierFormType;
    icon: string;
  }[] = [
    { label: "AUTO_ACCEPT_ORDERS", field: "autoAcceptOrders", icon: "done" },
    {
      label: "AUTO_PREPARE_ORDERS",
      field: "autoPrepareOrders",
      icon: "skillet",
    },
    {
      label:
        supplier?.lifecycleMode === "delivery-closure-v1"
          ? "AUTO_DELIVER_AND_CLOSE_ORDERS"
          : "AUTO_COMPLETE_ORDERS",
      field: "autoCompleteOrders",
      icon: "meal_delivery",
    },
  ];

  const expandedCutoffDropdownOptions = [
    { value: "0", label: t("SNACK_ADMIN.SETTINGS.CUTOFF_OPTIONS.SAME_DAY") },
    { value: "1", label: t("SNACK_ADMIN.SETTINGS.CUTOFF_OPTIONS.NEXT_DAY") },
  ];

  return (
    <>
      <div className={styles.deliverySettingsContainer}>
        <div style={{ flexDirection: "column" }}>
          <h3>{t("SNACK_ADMIN.SETTINGS.DELIVERY_METHODS")}</h3>
          <p>{t("SNACK_ADMIN.SETTINGS.DELIVERY_METHODS_DESCRIPTION")}</p>
        </div>
        {!!(
          supplier?.deliveryTypes.length &&
          !supplier.deliveryTypes.some(isSnackOrderWithServiceType)
        ) && (
          <div className="banner purpleBanner">
            <Icon
              name="star_shine"
              style={{ fontSize: "var(--wlcm-text-lg)" }}
            />
            <p>
              <strong>
                {t("SNACK_ADMIN.SETTINGS.VAT_UPDATE_BANNER.TITLE")}
              </strong>
              <br />
              {t("SNACK_ADMIN.SETTINGS.VAT_UPDATE_BANNER.TEXT")}
            </p>
          </div>
        )}
        <div>
          <form.Field name="deliveryTypes">
            {(field) => {
              const hasLegacyPickup = field.state.value.includes(
                "pickup" as SnackDeliveryMethod,
              );
              const hasPickupWithService = field.state.value.includes(
                SnackDeliveryMethod.PICKUP_WITH_SERVICE,
              );
              const hasPickupWithoutService = field.state.value.includes(
                SnackDeliveryMethod.PICKUP_WITHOUT_SERVICE,
              );
              const hasPickup =
                hasLegacyPickup ||
                hasPickupWithService ||
                hasPickupWithoutService;
              const setDeliveryTypes = (next: SnackDeliveryMethod[]) => {
                field.handleChange(next);
                if (!next.some(isSnackOrderPickupMethod)) {
                  restoreClearedTimes("pickup");
                }
              };
              const toggle = (method: SnackDeliveryMethod, checked: boolean) =>
                setDeliveryTypes(
                  (checked
                    ? Array.from(new Set([...field.state.value, method]))
                    : field.state.value.filter((v) => v !== method)
                  ).filter((v) => v !== ("pickup" as SnackDeliveryMethod)), // remove the legacy pickup method once supplier toggles the new settings
                );
              return (
                <>
                  <div
                    className={styles.iconContainer}
                    style={hasPickup ? {} : { background: "#0000001a" }}
                  >
                    <Icon
                      name="shopping_bag"
                      style={
                        hasPickup
                          ? {}
                          : { color: "var(--wlcm-color-darker-grey)" }
                      }
                    />
                  </div>
                  <div className={styles.deliveryTypeContent}>
                    <div className={styles.deliveryTypeHeader}>
                      <p>
                        <strong>
                          {t("SNACK_ADMIN.SETTINGS.OFFER_PICKUP")}
                        </strong>
                      </p>
                      <Toggle
                        label={hasPickup ? t("YES") : t("NO")}
                        checked={hasPickup}
                        onChange={(checked) => {
                          if (checked) {
                            toggle(
                              SnackDeliveryMethod.PICKUP_WITH_SERVICE,
                              true,
                            );
                          } else {
                            setDeliveryTypes(
                              field.state.value.filter(
                                (v) => !isSnackOrderPickupMethod(v),
                              ),
                            );
                          }
                        }}
                      />
                    </div>
                    {hasPickup && (
                      <div className={styles.deliveryTypeOptions}>
                        <div className={styles.checkboxGroup}>
                          <Checkbox
                            label={t(
                              "SNACK_ADMIN.SETTINGS.PICKUP_WITH_SERVICE",
                            )}
                            checked={hasPickupWithService}
                            onChange={(checked) =>
                              toggle(
                                SnackDeliveryMethod.PICKUP_WITH_SERVICE,
                                checked,
                              )
                            }
                          />
                          <Checkbox
                            label={t(
                              "SNACK_ADMIN.SETTINGS.PICKUP_WITHOUT_SERVICE",
                            )}
                            checked={hasPickupWithoutService}
                            onChange={(checked) =>
                              toggle(
                                SnackDeliveryMethod.PICKUP_WITHOUT_SERVICE,
                                checked,
                              )
                            }
                          />
                        </div>
                        {!hasLegacyPickup && (
                          <>
                            <div className={styles.timeInputsContainer}>
                              <form.Field
                                name="pickupSettings.pickupOpenAt"
                                validators={{ onChange: requireOpensAt }}
                              >
                                {(field) => (
                                  <TimeInput
                                    compact
                                    label={t("SNACK_ADMIN.SETTINGS.DURING")}
                                    value={field.state.value}
                                    onChange={(e) =>
                                      field.handleChange(
                                        e.target.value as TimeString,
                                      )
                                    }
                                    dangerText={field.state.meta.errors.toString()}
                                  />
                                )}
                              </form.Field>
                              <p>-</p>
                              <form.Field
                                name="pickupSettings.pickupClosedAt"
                                validators={{ onChange: requireClosesAt }}
                              >
                                {(field) => (
                                  <TimeInput
                                    compact
                                    label={t("SNACK_ADMIN.SETTINGS.DURING")}
                                    hideLabel
                                    value={field.state.value}
                                    onChange={(e) =>
                                      field.handleChange(
                                        e.target.value as TimeString,
                                      )
                                    }
                                    dangerText={field.state.meta.errors.toString()}
                                  />
                                )}
                              </form.Field>
                            </div>
                            <form.Field name="pickupSettings.pickupFee">
                              {(field) => (
                                <StrictNumberInput
                                  compact
                                  icon="money"
                                  label={t("SNACK_ADMIN.SETTINGS.FEE")}
                                  value={field.state.value}
                                  onChange={(e) =>
                                    field.handleChange(e.target.value as number)
                                  }
                                  dangerText={field.state.meta.errors.toString()}
                                  min={0}
                                  max={1000}
                                />
                              )}
                            </form.Field>
                            <form.Field name="pickupSettings.pickupLocation">
                              {(field) => (
                                <InputField
                                  compact
                                  icon="location_on"
                                  label={t(
                                    "SNACK_ADMIN.SETTINGS.PICKUP_LOCATION",
                                  )}
                                  value={field.state.value}
                                  onChange={(e) =>
                                    field.handleChange(e.target.value)
                                  }
                                  dangerText={field.state.meta.errors.toString()}
                                  maxLength={100}
                                />
                              )}
                            </form.Field>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            }}
          </form.Field>
        </div>
        <div>
          <form.Field name="deliveryTypes">
            {(field) => {
              const hasLegacyDelivery = field.state.value.includes(
                "delivery" as SnackDeliveryMethod,
              );
              const hasDeliveryWithService = field.state.value.includes(
                SnackDeliveryMethod.DELIVERY_WITH_SERVICE,
              );
              const hasDeliveryWithoutService = field.state.value.includes(
                SnackDeliveryMethod.DELIVERY_WITHOUT_SERVICE,
              );
              const hasDelivery =
                hasLegacyDelivery ||
                hasDeliveryWithService ||
                hasDeliveryWithoutService;
              const setDeliveryTypes = (next: SnackDeliveryMethod[]) => {
                field.handleChange(next);
                if (!next.some(isSnackOrderDeliveryMethod)) {
                  restoreClearedTimes("delivery");
                }
              };
              const toggle = (method: SnackDeliveryMethod, checked: boolean) =>
                setDeliveryTypes(
                  (checked
                    ? Array.from(new Set([...field.state.value, method]))
                    : field.state.value.filter((v) => v !== method)
                  ).filter((v) => v !== ("delivery" as SnackDeliveryMethod)), // remove the legacy delivery method once supplier toggles the new settings
                );
              return (
                <>
                  <div
                    className={styles.iconContainer}
                    style={hasDelivery ? {} : { background: "#0000001a" }}
                  >
                    <Icon
                      name="meal_delivery"
                      style={
                        hasDelivery
                          ? {}
                          : { color: "var(--wlcm-color-darker-grey)" }
                      }
                    />
                  </div>
                  <div className={styles.deliveryTypeContent}>
                    <div className={styles.deliveryTypeHeader}>
                      <p>
                        <strong>
                          {t("SNACK_ADMIN.SETTINGS.OFFER_DELIVERY")}
                        </strong>
                      </p>
                      <Toggle
                        label={hasDelivery ? t("YES") : t("NO")}
                        checked={hasDelivery}
                        onChange={(checked) => {
                          if (checked) {
                            toggle(
                              SnackDeliveryMethod.DELIVERY_WITH_SERVICE,
                              true,
                            );
                          } else {
                            setDeliveryTypes(
                              field.state.value.filter(
                                (v) => !isSnackOrderDeliveryMethod(v),
                              ),
                            );
                          }
                        }}
                      />
                    </div>
                    {hasDelivery && (
                      <div className={styles.deliveryTypeOptions}>
                        <div className={styles.checkboxGroup}>
                          <Checkbox
                            label={t(
                              "SNACK_ADMIN.SETTINGS.DELIVERY_WITH_SERVICE",
                            )}
                            checked={hasDeliveryWithService}
                            onChange={(checked) =>
                              toggle(
                                SnackDeliveryMethod.DELIVERY_WITH_SERVICE,
                                checked,
                              )
                            }
                          />
                          <Checkbox
                            label={t(
                              "SNACK_ADMIN.SETTINGS.DELIVERY_WITHOUT_SERVICE",
                            )}
                            checked={hasDeliveryWithoutService}
                            onChange={(checked) =>
                              toggle(
                                SnackDeliveryMethod.DELIVERY_WITHOUT_SERVICE,
                                checked,
                              )
                            }
                          />
                        </div>
                        {!hasLegacyDelivery && (
                          <>
                            <div className={styles.timeInputsContainer}>
                              <form.Field
                                name="deliverySettings.deliveryOpenAt"
                                validators={{ onChange: requireOpensAt }}
                              >
                                {(field) => (
                                  <TimeInput
                                    compact
                                    label={t("SNACK_ADMIN.SETTINGS.DURING")}
                                    value={field.state.value}
                                    onChange={(e) =>
                                      field.handleChange(
                                        e.target.value as TimeString,
                                      )
                                    }
                                    dangerText={field.state.meta.errors.toString()}
                                  />
                                )}
                              </form.Field>
                              <p>-</p>
                              <form.Field
                                name="deliverySettings.deliveryClosedAt"
                                validators={{ onChange: requireClosesAt }}
                              >
                                {(field) => (
                                  <TimeInput
                                    compact
                                    label={t("SNACK_ADMIN.SETTINGS.DURING")}
                                    hideLabel
                                    value={field.state.value}
                                    onChange={(e) =>
                                      field.handleChange(
                                        e.target.value as TimeString,
                                      )
                                    }
                                    dangerText={field.state.meta.errors.toString()}
                                  />
                                )}
                              </form.Field>
                            </div>
                            <form.Field name="deliverySettings.deliveryFee">
                              {(field) => (
                                <StrictNumberInput
                                  compact
                                  icon="money"
                                  label={t("SNACK_ADMIN.SETTINGS.FEE")}
                                  value={field.state.value}
                                  onChange={(e) =>
                                    field.handleChange(e.target.value as number)
                                  }
                                  dangerText={field.state.meta.errors.toString()}
                                  min={0}
                                  max={1000}
                                />
                              )}
                            </form.Field>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            }}
          </form.Field>
          <div className={styles.spacerDiv}>{/* Empty div for spacing */}</div>
        </div>
      </div>
      {supplier?.orderingMode === "cutoff-v1" ? (
        <form.Field name="closedOnWeekends">
          {(closedOnWeekendsField) => (
            <form.Field name="cutoffSettings">
              {(field) => (
                <div style={{ marginTop: "var(--wlcm-spacing-lg)" }}>
                  <OrderCutoffSettings
                    level="restaurant"
                    value={field.state.value}
                    savedValue={
                      supplier.cutoffSettings ?? DEFAULT_CUTOFF_SETTINGS
                    }
                    closedOnWeekends={closedOnWeekendsField.state.value}
                    savedClosedOnWeekends={supplier.closedOnWeekends ?? false}
                    onChange={(value) => {
                      onCutoffEdit(field.state.value);
                      field.handleChange(value);
                      setHasUnsavedChanges(true);
                    }}
                    onClosedOnWeekendsChange={(checked) => {
                      onClosedOnWeekendsEdit(closedOnWeekendsField.state.value);
                      closedOnWeekendsField.handleChange(checked);
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
              )}
            </form.Field>
          )}
        </form.Field>
      ) : (
        <div
          className={styles.deliverySettingsContainer}
          style={{ marginTop: "var(--wlcm-spacing-lg)" }}
        >
          <div style={{ flexDirection: "column" }}>
            <h3>{t("SNACK_ADMIN.SETTINGS.CUTOFF_ORDER_TIME")}</h3>
            <p>{t("SNACK_ADMIN.SETTINGS.CUTOFF_ORDER_TIME_DESCRIPTION")}</p>
          </div>
          <form.Field name="orderCutoffTime">
            {(field) => (
              <div style={{ alignItems: "end" }}>
                <SelectRow
                  name="orderCutoffTime"
                  label={t("SNACK_ADMIN.SETTINGS.OFFER_DELIVERY")}
                  hideLabel
                  value={
                    field.state.value
                      ? field.state.value === "24h"
                        ? 2
                        : 1
                      : 0
                  }
                  onChange={(value) => {
                    if (value === 1) {
                      field.handleChange(
                        (supplier?.orderCutoffTime &&
                        supplier.orderCutoffTime !== "24h"
                          ? supplier.orderCutoffTime
                          : "12:00") as TimeString,
                      );
                    } else if (value === 2) {
                      field.handleChange("24h");
                    } else {
                      field.handleChange(null);
                    }
                  }}
                  options={[
                    { value: 0, label: t("SNACK_ADMIN.SETTINGS.NO_CUTOFF") },
                    { value: 2, label: t("SNACK_ADMIN.SETTINGS.CUTOFF_24H") },
                    { value: 1, label: t("SNACK_ADMIN.SETTINGS.WITH_CUTOFF") },
                  ]}
                  style={{ marginBottom: 3 }}
                />
                {field.state.value && field.state.value !== "24h" && (
                  <>
                    <TimeInput
                      compact
                      label={t(
                        "SNACK_ADMIN.SETTINGS.CUTOFF_OPTIONS.EVERY_DAY_LABEL",
                      )}
                      hideLabel
                      value={field.state.value.split("+")[0] as TimeString}
                      onChange={(e) => {
                        const nextTime = e.target.value as TimeString;
                        const dayOffset = field.state.value?.split("+")[1];
                        field.handleChange(
                          dayOffset
                            ? (`${nextTime}+${dayOffset}` as ExpandedCutoffTimeString)
                            : nextTime,
                        );
                        setHasUnsavedChanges(true);
                      }}
                      dangerText={field.state.meta.errors.toString()}
                      style={{ flex: 0 }}
                    />
                    <Dropdown
                      compact
                      label={t(
                        "SNACK_ADMIN.SETTINGS.CUTOFF_OPTIONS.DROPDOWN_LABEL",
                      )}
                      options={expandedCutoffDropdownOptions}
                      value={
                        expandedCutoffDropdownOptions.find(
                          (op) => op.value === field.state.value?.split("+")[1],
                        ) ?? expandedCutoffDropdownOptions[0]
                      }
                      onChange={(op) => {
                        if (!field.state.value || field.state.value === "24h")
                          return;

                        if (op?.value === "0") {
                          field.handleChange(
                            field.state.value.split("+")[0] as TimeString,
                          );
                          setHasUnsavedChanges(true);
                        } else if (op?.value === "1") {
                          field.handleChange(
                            `${field.state.value.split("+")[0]}+1` as ExpandedCutoffTimeString,
                          );
                          setHasUnsavedChanges(true);
                        }
                      }}
                      placeholder={t("DEFAULT_SELECT.PLACEHOLDER")}
                      noResultsText={t("DEFAULT_SELECT.NO_RESULTS")}
                      inputBgColor="var(--wlcm-color-inactive-grey)"
                      styles={{ container: { flex: 0, minWidth: 300 } }}
                    />
                  </>
                )}
              </div>
            )}
          </form.Field>
        </div>
      )}
      <div style={{ marginTop: "var(--wlcm-spacing-lg)" }}>
        <h3 style={{ marginBottom: "var(--wlcm-spacing-sm)" }}>
          {t("SNACK_ADMIN.SETTINGS.AUTOMATION.HEADER")}
        </h3>
        <div
          className={companySettingsStyles.settingsGrid}
          style={{
            marginTop: 0,
            width: "max-content",
            maxWidth: "var(--wlcm-absolute-max-width)",
          }}
        >
          {automationToggles.map((toggle, index) => (
            <Fragment key={toggle.field}>
              <div className={companySettingsStyles.settingRow}>
                <div className={companySettingsStyles.settingLabel}>
                  <label htmlFor={`${toggle.label}_TOGGLE`}>
                    <Icon name={toggle.icon} />{" "}
                    {t(`SNACK_ADMIN.SETTINGS.AUTOMATION.${toggle.label}.LABEL`)}
                  </label>
                  <p
                    className={companySettingsStyles.description}
                    style={{ maxWidth: 500 }}
                  >
                    {t(
                      `SNACK_ADMIN.SETTINGS.AUTOMATION.${toggle.label}.DESCRIPTION`,
                    )}
                  </p>
                </div>
                <div className={companySettingsStyles.settingToggle}>
                  <form.Field name={toggle.field}>
                    {(field) => (
                      <Toggle
                        id={`${toggle.label}_TOGGLE`}
                        title={field.state.value ? t("YES") : t("NO")}
                        checked={field.state.value as boolean}
                        onChange={(checked) => field.handleChange(checked)}
                      />
                    )}
                  </form.Field>
                </div>
              </div>
              {index < automationToggles.length - 1 && (
                <div
                  className={companySettingsStyles.settingDivider}
                  style={{
                    backgroundColor: "var(--wlcm-color-grey-placeholder)",
                  }}
                />
              )}
            </Fragment>
          ))}
        </div>
      </div>
      <SupplierAdminsSection role="snack" />
    </>
  );
};

const ConnectMollie = () => {
  const { t } = useTranslation();
  const { addNotification } = useSnackbarStore();
  const { data: building } = useBuilding();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const [isConnectingMollie, setIsConnectingMollie] = useState(false);

  const handleConnectMollie = async () => {
    setIsConnectingMollie(true);
    try {
      const data = await getMollieAuthUrl();
      if ("alreadyConnected" in data && data.alreadyConnected) {
        addNotification(
          t("FOOD_ADMIN.SETTINGS.MOLLIE_ALREADY_CONNECTED"),
          NotificationType.SUCCESS,
        );
        return;
      }
      if ("authorizationUrl" in data && data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
        return;
      }
      addNotification(
        t("NOTIFICATIONS.SERVER_ERROR"),
        NotificationType.WARNING,
      );
    } catch {
      addNotification(
        t("NOTIFICATIONS.SERVER_ERROR"),
        NotificationType.WARNING,
      );
    } finally {
      setIsConnectingMollie(false);
    }
  };

  return (
    <div
      className="banner warningBanner"
      style={{ flexWrap: "wrap", marginBottom: "var(--wlcm-spacing-md)" }}
    >
      <Icon name="warning" />
      <div
        style={{
          flex: 1,
          minWidth: 250,
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          gap: 0,
        }}
      >
        <h4>{t("FOOD_ADMIN.SETTINGS.COMPLETE_SETUP_HEADER")}</h4>
        <p>{t("FOOD_ADMIN.SETTINGS.COMPLETE_SETUP_BODY")}</p>
      </div>
      <Button
        purple
        icon="arrow_forward"
        iconPosition="right"
        isLoading={isConnectingMollie}
        disabled={isConnectingMollie || supplier?.mollieConnected}
        onClick={handleConnectMollie}
        style={{ marginLeft: "auto" }}
      >
        {t("FOOD_ADMIN.SETTINGS.CONNECT_MOLLIE")}
      </Button>
    </div>
  );
};

export const FoodSettingsTab = ({
  form,
}: {
  form: ReactFormExtendedApi<SnackSupplierFormType, undefined>;
}) => {
  const { t } = useTranslation();
  const { data: building } = useBuilding();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const { data: supplierLogo } = useBuildingQuery(
    getImageOptions(supplier?.logoUrl),
  );
  const logoPreviewUrl = useStore(
    form.store,
    (state) => state.values.logo?.previewUrl,
  );
  // A new, unsaved pick is a blob: URL. Otherwise the form can hold the raw
  // stored logoUrl (a path or an old storage link), so use the signed URL.
  // Locally the emulator can't sign, so getImage returns "" and the poster shows the name.
  const supplierLogoUrl = logoPreviewUrl?.startsWith("blob:")
    ? logoPreviewUrl
    : supplierLogo;

  return (
    <div style={{ marginTop: "var(--wlcm-spacing-md)" }}>
      {!!(supplier && !supplier.mollieConnected) && <ConnectMollie />}
      {supplier?.wlcmFoodSettings && (
        <>
          <h3>{t("FOOD_ADMIN.SETTINGS.LUNCH_HOURS")}</h3>
          <p style={{ opacity: 0.5, marginBottom: "var(--wlcm-spacing-xs)" }}>
            {t("FOOD_ADMIN.SETTINGS.LUNCH_HOURS_DESCRIPTION")}
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "start",
              gap: "var(--wlcm-spacing-xs)",
            }}
          >
            <form.Field
              name="wlcmFoodSettings.openingLunchTime"
              validators={{
                onChangeListenTo: ["wlcmFoodSettings.closingLunchTime"],
                onChange: ({ value, fieldApi }) => {
                  const closingTime = fieldApi.form.getFieldValue(
                    "wlcmFoodSettings.closingLunchTime",
                  );
                  if (!isTimeString(value)) {
                    return t("VALIDATION.REQUIRED", {
                      field: t(
                        "BUILDING_SETTINGS.BOOKING.EDIT_ROOM.OPENING_HOURS.OPENS_AT",
                      ),
                    });
                  }
                  // The closing field shows its own error
                  if (!isTimeString(closingTime)) return undefined;
                  const isValid = isBefore(
                    timeInputStringToDate(value as TimeString),
                    timeInputStringToDate(closingTime as TimeString),
                  );
                  return isValid
                    ? undefined
                    : t("FOOD_ADMIN.SETTINGS.LUNCH_HOURS_INVALID_OPENING");
                },
              }}
            >
              {(field) => (
                <TimeInput
                  compact
                  hideLabel
                  label={t("BUILDING_SETTINGS.BOOKING.EDIT_ROOM.WEEKDAYS.FROM")}
                  value={field.state.value}
                  maxTime={field.form.getFieldValue(
                    "wlcmFoodSettings.closingLunchTime",
                  )}
                  onChange={(e) =>
                    field.handleChange(e.target.value as TimeString)
                  }
                  dangerText={field.state.meta.errors.toString()}
                />
              )}
            </form.Field>
            <p style={{ marginTop: "var(--wlcm-spacing-sm)" }}>-</p>
            <form.Field
              name="wlcmFoodSettings.closingLunchTime"
              validators={{
                onChangeListenTo: ["wlcmFoodSettings.openingLunchTime"],
                onChange: ({ value, fieldApi }) => {
                  const openingTime = fieldApi.form.getFieldValue(
                    "wlcmFoodSettings.openingLunchTime",
                  );
                  if (!isTimeString(value)) {
                    return t("VALIDATION.REQUIRED", {
                      field: t(
                        "BUILDING_SETTINGS.BOOKING.EDIT_ROOM.OPENING_HOURS.CLOSES_AT",
                      ),
                    });
                  }
                  // The opening field shows its own error
                  if (!isTimeString(openingTime)) return undefined;
                  const isValid = isBefore(
                    timeInputStringToDate(openingTime as TimeString),
                    timeInputStringToDate(value as TimeString),
                  );
                  return isValid
                    ? undefined
                    : t("FOOD_ADMIN.SETTINGS.LUNCH_HOURS_INVALID_CLOSING");
                },
              }}
            >
              {(field) => (
                <TimeInput
                  compact
                  hideLabel
                  label={t("TO")}
                  value={field.state.value}
                  minTime={field.form.getFieldValue(
                    "wlcmFoodSettings.openingLunchTime",
                  )}
                  onChange={(e) =>
                    field.handleChange(e.target.value as TimeString)
                  }
                  dangerText={field.state.meta.errors.toString()}
                />
              )}
            </form.Field>
          </div>
          <h3 style={{ marginTop: "var(--wlcm-spacing-md)" }}>
            {t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH")}
          </h3>
          <p style={{ opacity: 0.5, marginBottom: "var(--wlcm-spacing-xs)" }}>
            {t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_DESCRIPTION")}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-sm)",
            }}
          >
            <form.Field name="wlcmFoodSettings.guestLunchGrossPrice">
              {(field) => (
                <>
                  <Toggle
                    label={t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_TOGGLE")}
                    title={!!field.state.value ? t("YES") : t("NO")}
                    checked={!!field.state.value}
                    onChange={(checked) =>
                      field.handleChange(
                        checked
                          ? supplier?.wlcmFoodSettings?.guestLunchGrossPrice ||
                              100
                          : null,
                      )
                    }
                  />
                  {field.state.value && (
                    <StrictNumberInput
                      label={t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_PRICE")}
                      icon="money"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      append={
                        <span style={{ marginRight: "var(--wlcm-spacing-sm)" }}>
                          kr
                        </span>
                      }
                      min={1}
                      max={1_000}
                      allowDecimals={2}
                    />
                  )}
                  {!!field.state.value && (
                    <GuestLunchQr
                      supplierName={supplier?.name}
                      supplierLogoUrl={supplierLogoUrl}
                    />
                  )}
                </>
              )}
            </form.Field>
          </div>
          {supplier.wlcmFoodSettings.hasMultipleVariants && (
            <>
              <h3 style={{ marginTop: "var(--wlcm-spacing-md)" }}>
                {t("FOOD_ADMIN.SETTINGS.LUNCH_PRODUCTS")}
              </h3>
              <p style={{ opacity: 0.5 }}>
                {t("FOOD_ADMIN.SETTINGS.LUNCH_PRODUCTS_DESCRIPTION")}
              </p>
              <div className="highlightedInfo">
                <Icon name="info" />
                <p>{t("FOOD_ADMIN.SETTINGS.LUNCH_PRODUCTS_INFO")}</p>
              </div>
              <div
                style={{
                  marginTop: "var(--wlcm-spacing-sm)",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "stretch",
                  gap: "var(--wlcm-spacing-sm)",
                }}
              >
                {supplier.wlcmFoodSettings.lunchVariants
                  ?.toSorted((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                  .map((variant) => (
                    <div
                      key={variant.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--wlcm-spacing-sm)",
                        padding: "var(--wlcm-spacing-md)",
                        borderRadius: "var(--wlcm-border-radius-xs)",
                        background: `${variant.color}1A`,
                      }}
                    >
                      <Icon
                        name={variant.icon}
                        style={{
                          fontSize: "var(--wlcm-text-xl)",
                          opacity: 0.5,
                          color: variant.color,
                        }}
                      />
                      <h4>{variant.name}</h4>
                      <div
                        style={{
                          marginLeft: "var(--wlcm-spacing-sm)",
                          width: "1.5rem",
                          height: "1.5rem",
                          borderRadius: 100,
                          background: variant.color,
                        }}
                      ></div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </>
      )}
      <SupplierAdminsSection role="food" />
    </div>
  );
};

const EmailChip = ({
  email,
  index,
  dark,
  onRemove,
  onPromote,
}: {
  email: string;
  index: number;
  dark?: boolean;
  onRemove: (email: string) => void;
  onPromote: (email: string) => void;
}) => (
  <motion.div layout style={{ display: "inline-flex" }}>
    <Chip
      id={email}
      label={`${index + 1}. ${email}`}
      active
      dark={dark}
      style={
        index === 0
          ? {
              background: `color-mix(in srgb, var(--wlcm-color-blue) ${dark ? 40 : 30}%, transparent)`,
            }
          : undefined
      }
      onClick={(_e, id) => onPromote(id)}
      onActionClick={(id) => onRemove(id)}
    />
  </motion.div>
);

// `leadingNode` renders next to the InputField, aligned to the input line (not the label)
const EmailChipsField = ({
  emails,
  onAdd,
  onRemove,
  onPromote,
  leadingNode,
}: {
  emails: string[];
  onAdd: (email: string) => void;
  onRemove: (email: string) => void;
  onPromote: (email: string) => void;
  leadingNode?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const isFull = emails.length >= MAX_ADMIN_EMAILS;

  const commit = () => {
    const next = draft.trim().toLowerCase();
    if (
      !next ||
      emails.some((current) => current.toLowerCase() === next) ||
      isFull ||
      next.length > MAX_EMAIL_LENGTH
    )
      return;
    if (!EMAIL_REGEX.test(next)) {
      setError(t("VALIDATION.INVALID_EMAIL_FORMAT"));
      return;
    }
    onAdd(next);
    setDraft("");
    setError(undefined);
  };

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--wlcm-spacing-sm)",
        }}
      >
        {leadingNode}
        <InputField
          icon="mail"
          label={`${t(
            isFull
              ? "SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.MAX_REACHED"
              : "SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.ADD_EMAIL_LABEL",
          )} ${emails.length}/${MAX_ADMIN_EMAILS}`}
          placeholder="ola@example.com"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(undefined);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // don't submit the whole form
              commit();
            }
          }}
          disabled={isFull}
          maxLength={MAX_EMAIL_LENGTH}
          dangerText={error}
          style={{ flex: 1 }}
          append={
            <Button
              circular
              icon="add"
              disabled={isFull || !draft.trim()}
              onClick={commit}
              style={{ marginRight: -8 }}
            >
              <span className="sr-only">{t("ADD")}</span>
            </Button>
          }
        />
      </div>
      {emails.length === 0 ? (
        <p
          style={{
            opacity: 0.7,
            marginTop: 4,
            fontSize: "var(--wlcm-text-sm)",
          }}
        >
          {t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.NO_EMAILS")}
        </p>
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--wlcm-spacing-xs)",
            marginTop: 4,
          }}
        >
          {emails.map((email, index) => (
            <EmailChip
              key={email}
              email={email}
              index={index}
              onRemove={onRemove}
              onPromote={onPromote}
            />
          ))}
        </div>
      )}
      {emails.length > 1 && (
        <p
          style={{
            opacity: 0.7,
            marginTop: 4,
            fontSize: "var(--wlcm-text-sm)",
          }}
        >
          {t("SNACK_ADMIN.SETTINGS.CONTACT_DETAILS.PROMOTE_HINT")}
        </p>
      )}
    </div>
  );
};
